import { supabase } from '../../shared/services/supabase';
import { CustomerCustomTodo } from '../../shared/types';

export class CustomerTodoService {
  /**
   * Lädt alle aktiven To-Dos für einen Kunden
   * Optional gefiltert nach aktuellem Monat
   */
  async getCustomerTodos(customerId: string, currentMonth?: number): Promise<CustomerCustomTodo[]> {
    try {
      const { data, error } = await supabase
        .from('customer_custom_todos')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading customer todos:', error);
        return [];
      }

      // Filtere nach aktiven Monaten, wenn ein Monat angegeben ist
      if (currentMonth !== undefined && data) {
        return data.filter(todo => {
          // Wenn active_months null ist, ist das To-Do immer aktiv
          if (!todo.active_months) return true;
          
          // Prüfe ob der aktuelle Monat in den aktiven Monaten enthalten ist
          return todo.active_months.includes(currentMonth);
        });
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCustomerTodos:', error);
      return [];
    }
  }

  /**
   * Erstellt ein neues To-Do für einen Kunden
   * Erweitert um requires_confirmation und active_months
   */
  async createTodo(
    customerId: string, 
    title: string, 
    description?: string,
    requiresConfirmation?: boolean,
    activeMonths?: number[] | null
  ): Promise<CustomerCustomTodo | null> {
    try {
      // Hole die höchste sort_order für diesen Kunden
      const { data: maxOrderData } = await supabase
        .from('customer_custom_todos')
        .select('sort_order')
        .eq('customer_id', customerId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextSortOrder = maxOrderData && maxOrderData.length > 0 
        ? (maxOrderData[0].sort_order || 0) + 1 
        : 0;

      const { data, error } = await supabase
        .from('customer_custom_todos')
        .insert([{
          customer_id: customerId,
          title: title.trim(),
          description: description?.trim() || null,
          is_active: true,
          sort_order: nextSortOrder,
          requires_confirmation: requiresConfirmation || false,
          active_months: activeMonths || null
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating todo:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createTodo:', error);
      return null;
    }
  }

  /**
   * Aktualisiert ein To-Do
   * Erweitert um requires_confirmation und active_months
   */
  async updateTodo(
    todoId: string, 
    updates: Partial<Pick<CustomerCustomTodo, 'title' | 'description' | 'is_active' | 'requires_confirmation' | 'active_months'>>
  ): Promise<boolean> {
    try {
      const updateData: any = {
        ...updates,
        title: updates.title?.trim(),
        description: updates.description?.trim() || null
      };

      // Stelle sicher, dass active_months korrekt gespeichert wird
      if ('active_months' in updates) {
        updateData.active_months = updates.active_months || null;
      }

      const { error } = await supabase
        .from('customer_custom_todos')
        .update(updateData)
        .eq('id', todoId);

      if (error) {
        console.error('Error updating todo:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateTodo:', error);
      return false;
    }
  }

  /**
   * Löscht ein To-Do (soft delete - setzt is_active auf false)
   */
  async deleteTodo(todoId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('customer_custom_todos')
        .update({ is_active: false })
        .eq('id', todoId);

      if (error) {
        console.error('Error deleting todo:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteTodo:', error);
      return false;
    }
  }

  /**
   * Ändert die Reihenfolge der To-Dos
   */
  async reorderTodos(customerId: string, todoIds: string[]): Promise<boolean> {
    try {
      // Update alle To-Dos mit neuer sort_order
      const updates = todoIds.map((todoId, index) => 
        supabase
          .from('customer_custom_todos')
          .update({ sort_order: index })
          .eq('id', todoId)
          .eq('customer_id', customerId)
      );

      const results = await Promise.all(updates);
      
      // Prüfe ob alle Updates erfolgreich waren
      const hasError = results.some(result => result.error);
      if (hasError) {
        console.error('Error reordering todos');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in reorderTodos:', error);
      return false;
    }
  }

  /**
   * Lädt To-Dos für mehrere Kunden (für Batch-Loading)
   * Optional gefiltert nach aktuellem Monat
   */
  async getTodosForCustomers(customerIds: string[], currentMonth?: number): Promise<Record<string, CustomerCustomTodo[]>> {
    try {
      if (customerIds.length === 0) return {};

      const { data, error } = await supabase
        .from('customer_custom_todos')
        .select('*')
        .in('customer_id', customerIds)
        .eq('is_active', true)
        .order('customer_id', { ascending: true })
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading todos for customers:', error);
        return {};
      }

      // Gruppiere To-Dos nach customer_id
      const groupedTodos: Record<string, CustomerCustomTodo[]> = {};
      (data || []).forEach(todo => {
        // Filtere nach aktiven Monaten, wenn ein Monat angegeben ist
        if (currentMonth !== undefined && todo.active_months && !todo.active_months.includes(currentMonth)) {
          return; // Skip dieses To-Do
        }

        if (!groupedTodos[todo.customer_id]) {
          groupedTodos[todo.customer_id] = [];
        }
        groupedTodos[todo.customer_id].push(todo);
      });

      return groupedTodos;
    } catch (error) {
      console.error('Error in getTodosForCustomers:', error);
      return {};
    }
  }

  /**
   * NEU: Prüft ob alle bestätigungspflichtigen To-Dos bestätigt wurden
   */
  async checkConfirmationRequirements(
    customerId: string, 
    confirmedTodoIds: string[] = [],
    currentMonth?: number
  ): Promise<{ allConfirmed: boolean; missingTodos: CustomerCustomTodo[] }> {
    try {
      // Lade alle To-Dos für diesen Kunden (ggf. gefiltert nach Monat)
      const todos = await this.getCustomerTodos(customerId, currentMonth);
      
      // Filtere nur bestätigungspflichtige To-Dos
      const requiresConfirmation = todos.filter(todo => todo.requires_confirmation);
      
      // Prüfe welche noch nicht bestätigt wurden
      const missingTodos = requiresConfirmation.filter(
        todo => !confirmedTodoIds.includes(todo.id)
      );

      return {
        allConfirmed: missingTodos.length === 0,
        missingTodos
      };
    } catch (error) {
      console.error('Error checking confirmation requirements:', error);
      return {
        allConfirmed: true, // Im Fehlerfall nicht blockieren
        missingTodos: []
      };
    }
  }
}

// Singleton instance
export const customerTodoService = new CustomerTodoService();